import type { Option } from '@usealloy/component-library';
import type {
  AggregationType,
  TimeframeUnit,
} from 'flow/store/engine/constants';
import type {
  AggregationComponent,
  AggregationFilter,
} from 'flow/store/engine/types';

export type AggregationObjectType = 'entity' | 'transaction' | 'login';

export const AggregationFilterOperator = {
  LT: 'lt',
  LTE: 'lte',
  GT: 'gt',
  GTE: 'gte',
  EQ: 'eq',
  NE: 'ne',
  BT: 'bt',
  IN: 'in',
} as const;

export type AggregationFilterOperatorType =
  (typeof AggregationFilterOperator)[keyof typeof AggregationFilterOperator];

export type BaseAggregationUiDataType =
  | 'integer'
  | 'boolean'
  | 'string'
  | 'select'
  | 'decimal'
  | 'timestamp'
  | 'date';

export type AggregationFilterUiDataType =
  | BaseAggregationUiDataType
  | 'published_attribute'
  | 'device_detail';

export type AggregationPublishedAttribute = {
  published_attribute_token: string;
  published_attribute_destination_column: string;
  published_attribute_ui_data_type: BaseAggregationUiDataType;
  published_attribute_evaluation_payload_path: string;
  published_attribute_description: string;
  published_attribute_name?: string;
  data_type?: string;
  contains_pii?: boolean;
  aggregations: AggregationType[];
  filter_operators: AggregationFilterOperatorType[];
  possible_values: string[] | null;
};

export type AggregationDeviceDetail = {
  device_detail_field: string;
  display_name: string;
  ui_data_type: BaseAggregationUiDataType;
  contains_pii: boolean;
  aggregations: AggregationType[];
  filter_operators: AggregationFilterOperatorType[];
  possible_values: string[] | null;
};

export type AggregationColumn = {
  display_name: string;
  column_slug: string;
  user_filterable: boolean;
  user_aggregable: boolean;
  aggregations: AggregationType[];
  filter_operators: AggregationFilterOperatorType[];
  ui_data_type: AggregationFilterUiDataType;
  main_identifier_precedence: number | null;
  possible_values: string[] | null;
  // Hash salt. Two columns can only be compared when this matches — a salted
  // column paired with an unsalted one compares a hash to plaintext and always
  // matches nothing.
  wrapped_encryption_key_title?: string | null;
  published_attribute_metadata?: {
    published_attributes: AggregationPublishedAttribute[];
  } | null;
  device_detail_metadata?: {
    device_detail_fields: AggregationDeviceDetail[];
  } | null;
};

export type AggregationFilterOption = Option & {
  ui_data_type: AggregationFilterUiDataType;
  filter_operators: AggregationFilterOperatorType[];
  column_slug: string;
  /** Source column display_name — PA group heading in buildOptionGroups. */
  display_name: string;
  published_attribute_description?: string | null;
  published_attribute_ui_data_type?: BaseAggregationUiDataType;
  published_attribute_name?: string;
  data_type?: string;
  contains_pii?: boolean;
  possible_values: string[] | null;
  device_detail_ui_data_type?: BaseAggregationUiDataType;
  wrapped_encryption_key_title?: string | null;
};

export type AggregationColumnOption = AggregationFilterOption & {
  aggregations: AggregationType[];
};

export type AggregationConfig = {
  columns: AggregationColumn[];
  objectDisplayName: string;
  objectType: AggregationObjectType;
  publicVersion: number;
};

export type AggregationConfigByObjectType = Partial<
Record<AggregationObjectType, AggregationConfig>
>;

export type OptionGroup<T> = {
  label: string;
  value: null;
  options: T[];
};

export type AggregationSourceData = {
  name: string;
  description: string;
  aggregation: AggregationComponent['aggregation'];
  scope: string;
  filters: AggregationFilter[];
  timeframeIntervalStart: number;
  timeframeIntervalEnd: number;
  timeframeUnit: TimeframeUnit;
  showTimeframeFilter: boolean;
};

/** Keys for aggregation fields that use the select/dropdown (handleSelectChange). */
export type AggregationSelectFieldKey =
  | keyof Pick<AggregationSourceData, 'scope' | 'timeframeUnit'>
  | 'columnSlug' | 'aggregationType';

export type AggregationValidationErrors = {
  name?: string;
  description?: string;
};
